import { defineStore } from 'pinia'

import type { EditorSettings } from '@/stores/interfaces/Editor'
import type { IGoalSettings, IHeroContent, LanguageType } from '@/api/interfaces'
import { BackgroundTheme, type IEditorSettings, LANGUAGES } from '@/api/interfaces'
import { getEditorSettings, updateEditorSettings } from '@/api'
import { dispatchMessage, EVENT_TYPES } from '@/composables/iframe'
import { inIFrame } from '@/helpers/utils'

export const useEditorStore = defineStore('editor', {
  state: (): EditorSettings => ({
    editor: {
      background: {
        theme: BackgroundTheme.LIGHT,
        icon: 'leaves',
        themeColor: {
          background: '#8852cb',
          text: '#FFFFFF',
        },
      },
      hero: {
        style: 'center',
        logo: {
          url: '',
          visible: true,
        },
        image: {
          url: '',
          visible: true,
          overlayColor: '#FFFFFF',
          textColor: '#212121',
        },
        content: {
          ...LANGUAGES.reduce(
            (acc, lang) => {
              acc[lang] = {
                title: '',
                subtitle: '',
              }
              return acc
            },
            {} as Record<LanguageType, IHeroContent>,
          ),
        },
        links: {
          facebook: '',
          instagram: '',
          linkedin: '',
          website: '',
          twitter: '',
          tiktok: '',
          youtube: '',
        },
        memberSince: {
          visible: true,
        },
      },
      icon: {
        style: 'simple',
        impactColors: {
          carbon: {
            backgroundColor: '#FFB339',
            textColor: '#212121',
          },
          trees: {
            backgroundColor: '#49E2A7',
            textColor: '#212121',
          },
          plastic: {
            backgroundColor: '#1FAEF0',
            textColor: '#212121',
          },
          kelp: {
            backgroundColor: '#3D9B98',
            textColor: '#212121',
          },
          water: {
            backgroundColor: '#6074FF',
            textColor: '#FCFCFC',
          },
          bees: {
            backgroundColor: '#EACA78',
            textColor: '#FCFCFC',
          },
        },
        impactDisplay: {
          carbon: true,
          trees: true,
          plastic: true,
          kelp: true,
          water: true,
          bees: true,
          monthsEarthPositive: true,
          hideZeroValues: true,
        },
      },
      goalsAchievements: {
        ...LANGUAGES.reduce(
          (acc, lang) => {
            acc[lang] = {
              goals: [],
              achievements: [],
            }
            return acc
          },
          {} as Record<LanguageType, IGoalSettings>,
        ),
      },
    },
    previewMode: false,
    loading: false,
    activeDevice: 'desktop',
    publishLoading: false,
    lang: 'en',
    valid: true,
  }),
  getters: {},
  actions: {
    async fetchEditorSettings(id: string) {
      try {
        const { data } = await getEditorSettings(id)
        this.$patch({ editor: { ...data.publicPageEditorSettings } })

        if (inIFrame()) {
          dispatchMessage(window.parent, {
            type: EVENT_TYPES.UPDATE_EDITOR_STORE,
            payload: { editor: { ...data.publicPageEditorSettings } },
          })
        }
      } catch (e) {
        console.error(e)
      }
    },
    async saveEditorSettings(payload: Partial<IEditorSettings>) {
      try {
        const { data } = await updateEditorSettings(payload)
        this.$patch({ editor: { ...data } })
      } catch (e) {
        console.error(e)
        return Promise.reject(e)
      }
    },
  },
})
